import { createFileRoute, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { HStack, VStack } from '@/components/Layout';
import { ViewportLoader } from '@/components/Loading';
import { ScrollableTabsList } from '@/components/Tabs';
import { Tabs, TabsTrigger } from '@/components/ui/tabs';
import { featureFlags } from '@/lib/env';
import { EmptyMdmState } from './-components/EmptyMdmState';
import { MdmTabContent } from './-components/MdmTabContent';
import { useDeviceMdm } from './-query';
import { MOCK_MDM_DATA_LIST } from './-query/mockData';
import type { MdmStructuredData } from './-query/type';
import { getMdmTypeLabel, MDM_TYPES, type MdmTypeValue } from './-query/utils';
export const Route = createFileRoute('/_staff/devices/$id/_layout/mdm/')({
  component: DeviceMdmTab
});
const TAB_VALUES = [...MDM_TYPES, 'EmptyState'] as const;
type TabValue = (typeof TAB_VALUES)[number];
const getMdmDataByType = (mdmType: MdmTypeValue): MdmStructuredData | null => {
  return MOCK_MDM_DATA_LIST.find(mdm => mdm.mdmType === mdmType && mdm.sections && mdm.sections.length > 0) ?? null;
};
function DeviceMdmTab() {
  const {
    id: deviceId
  } = useParams({
    from: '/_staff/devices/$id/_layout/mdm/'
  });
  const {
    data: mdmData,
    isPending: isMdmPending
  } = useDeviceMdm(deviceId);
  if (isMdmPending || !mdmData) {
    return <ViewportLoader />;
  }
  const mdmDataList = mdmData.mdmDataList;
  const validMdmDataList = mdmDataList.filter(mdm => mdm.sections && mdm.sections.length > 0);
  const latestMdm = [...validMdmDataList].sort((a, b) => {
    const dateB = b.latestSyncDate ? new Date(b.latestSyncDate).getTime() : 0;
    const dateA = a.latestSyncDate ? new Date(a.latestSyncDate).getTime() : 0;
    return dateB - dateA;
  })[0] ?? null;
  const isMdmPreviewEnabled = featureFlags.check('MDM_PREVIEW');
  const hasData = latestMdm !== null;
  return <VStack className="h-full">
      {hasData ? <MdmTabContent mdmData={latestMdm} /> : isMdmPreviewEnabled ? <MdmPreviewContent /> : <EmptyMdmState />}
    </VStack>;
}
function MdmPreviewContent() {
  const [selectedTab, setSelectedTab] = useState<TabValue>('Intune');
  return <VStack className="h-full">
      <HStack className="px-12 pt-3 pb-3 border-b border-neutral-muted-border">
        <Tabs value={selectedTab} onValueChange={setSelectedTab} validValues={TAB_VALUES} className="flex-1 min-w-0">
          <ScrollableTabsList>
            {MDM_TYPES.map(mdmType => <TabsTrigger key={mdmType} value={mdmType}>
                {getMdmTypeLabel(mdmType)}
              </TabsTrigger>)}
            <TabsTrigger value="EmptyState">Empty State</TabsTrigger>
          </ScrollableTabsList>
        </Tabs>
      </HStack>

      <div className="flex-1 overflow-auto">
        {selectedTab === 'EmptyState' ? <EmptyMdmState /> : <MdmTabContent mdmData={getMdmDataByType(selectedTab)} />}
      </div>
    </VStack>;
}